// #642 — the Order of Go model.
//
// Pure functions: take the raw entities the app has already prefetched
// (rides, breaks, classes, people, horses, tests) plus the live status map from
// the batch-status poll, and produce the ONE flat, time-ordered list the
// announcer reads top-to-bottom.
//
// Why the merge happens here and not on the server (docs/score/announcer.md,
// "Breaks"): reconcile deliberately never writes a break as a ride — a break has
// no rider, no horse and must never be scored, placed or emailed. So `GET /ride`
// and `GET /break` are two separate lists and the interleave is the client's job.
//
// Ride rows also carry no denormalized names — reconcile stores riderId /
// horseId / classId / testId only — so every display string is joined here from
// the prefetched entity caches.

import {
  RIDE_STATUSES,
  isQualifying,
  resolveRingTruth,
  resolveNextRide,
  deriveRideTermReason,
} from "score-common";
import { venueDayKey } from "./formatTime.js";

// #2131 — re-exported so OrderOfGo.jsx reads ring truth through THIS file
// (the app's one seam onto the shared module, matching how buildOrderOfGoRows
// itself resolves CURRENT/ON_DECK below) rather than importing score-common
// directly in two places for the same question.
export { resolveRingTruth };

export const ROW_KIND = {
  CLASS_HEADER: "CLASS_HEADER",
  RIDE: "RIDE",
  BREAK: "BREAK",
  // #892 — the "all rides" view's per-day grouping row, inserted by
  // groupRowsByDay() below. Never produced by buildOrderOfGoRows itself.
  DAY_HEADER: "DAY_HEADER",
};

// #2131 (docs/score/ring-truth.md rule 7) — where a ride sits relative to
// "now" in the running order. Only ever assigned to at most one row each.
// Deliberately just two roles now, not #858's three: ring-truth defines
// exactly ONE next-ride concept (the immediate on-deck ride), so a second
// "NEXT after that" chip would be exactly the kind of independent opinion
// this card retires — nothing sources it from the module. RideRow.jsx still
// imports this from here.
export const RIDE_ROLE = {
  CURRENT: "CURRENT",
  ON_DECK: "ON_DECK",
};

// Reconcile only STAMPS a status for scratches; scoring stamps the rest. A ride
// that hasn't been touched has no `status` field at all, so treat missing as
// PENDING rather than rendering an empty badge.
export function normalizeStatus(status) {
  return status || RIDE_STATUSES.PENDING;
}

// Sort missing times LAST rather than first (an unscheduled ride shouldn't jump
// to the top of the announcer's list). Matches GET /break's server-side rule.
const timeKey = (t) => (t ? String(t) : "￿");

const byId = (rows = []) => new Map((rows || []).filter(Boolean).map((r) => [r.id, r]));

function personName(person) {
  if (!person) return "";
  const full = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
  return full || person.name || "";
}

function joinParts(parts, sep) {
  return parts.filter((p) => p != null && p !== "").join(sep);
}

// #525 registry fields (breed/height/color/.../foalYear) are ALL optional — FVD
// only carries them when HR.hdf has them. "8yo" only for a sane foalYear;
// otherwise "" so the caller can drop the whole line rather than show "NaNyo".
function ageFrom(foalYear, referenceYear) {
  const fy = Number(foalYear);
  if (!Number.isFinite(fy) || fy <= 0) return "";
  const age = referenceYear - fy;
  return age > 0 && age < 60 ? `${age}yo` : "";
}

// Same field fallbacks the printed OOG report uses
// (packages/report-defs/src/score/announcer-order-of-go.js buildRide), so the
// screen and the paper fallback say the same thing about the same ride.
// EXCEPTION (#2070): the printed report's buildRide calls
// deriveRideTermReason(ride) with no sheets argument, deliberately, per
// #2058 — this screen now passes sheetsByRideId too, so a ride terminated
// ONLY via a sheet-level eliminations[] entry resolves a chip here that the
// paper copy still omits.
function buildRideRow(
  ride,
  { classesById, peopleById, horsesById, testsById, statuses, sheetsByRideId },
) {
  const cls = classesById.get(ride.classId);
  const rider = peopleById.get(ride.riderId);
  const horse = horsesById.get(ride.horseId);
  const test = testsById.get(ride.testId);

  // #643 — the ride-detail accordion's script. Composed with the SAME field
  // fallbacks and join order as the printed OOG report
  // (packages/report-defs/src/score/announcer-order-of-go.js buildRide), per
  // docs/score/announcer.md: "render the same fields in the same order ... so
  // the announcer's eye already knows where to look." Owner is a person
  // reference (horse.ownerId), resolved from the SAME prefetched people cache
  // used for the rider — no extra fetch. Each joined string is "" when every
  // part is missing, so RideRow can drop the whole line — never a bare label
  // over an empty value.
  let owner = "";
  if (horse?.ownerId) owner = personName(peopleById.get(horse.ownerId));
  if (!owner) owner = horse?.owner || horse?.ownerName || "";

  const referenceYear = new Date().getFullYear();
  const horseDetail = joinParts(
    [horse?.breed, horse?.height, horse?.color, horse?.sex, ageFrom(horse?.foalYear, referenceYear)],
    " · ",
  );
  const pedigree =
    horse?.sire || horse?.dam
      ? joinParts(
          [
            `${horse?.sire || "?"} × ${horse?.dam || "?"}`,
            horse?.damsSire ? `dam's sire ${horse.damsSire}` : "",
          ],
          " · ",
        )
      : "";
  const ownerLine = joinParts([owner, horse?.breeder ? `breeder ${horse.breeder}` : ""], " · ");
  // #726 — drop ZIP from the "Rider From" line; city/state only.
  const address = joinParts([rider?.city, rider?.state], ", ");

  return {
    kind: ROW_KIND.RIDE,
    id: ride.id,
    // #805 — foxbox now emits genuine UTC; render/sort the stored value directly.
    scheduledTime: ride.scheduledTime,
    sortKey: timeKey(ride.scheduledTime),
    // #892 — venue-local calendar day, for the all-rides/today-only toggle's
    // grouping. null when there's no time to derive one from (an unscheduled
    // ride) — groupRowsByDay() below decides what that falls back to.
    dayKey: venueDayKey(ride.scheduledTime),
    classId: ride.classId,
    className: cls?.className || cls?.name || "",
    classNumber: cls?.classNumber || cls?.number || "",
    competitorNumber: ride.competitorNumber || ride.rideNumber || "",
    riderName: personName(rider) || ride.riderName || "",
    horseName: horse?.name || ride.horseName || "",
    // #1878 — prefer the catalog's own clean name over FVD's raw testCode.
    testName: test?.name || test?.testCode || test?.level || "",
    // #806 — ride.qualifying is FVD's chrQualifyID text (e.g. "No" | "Open" |
    // "Adult Amateur" | "JR/YR"), not a boolean. Boolean(ride.qualifying)
    // treated the literal string "No" as truthy, so every ride showed the
    // QUALIFYING badge. isQualifying() (score-common) is the shared fix, also
    // used by report-defs's #686 divisionLabel().
    qualifying: isQualifying(ride.qualifying),
    // #2070 — resolve via score-common's shared deriveRideTermReason (the
    // SAME 4-tier precedence every other consuming surface already reads
    // through: terminationReason -> ride-level No Show -> legacy
    // ride.termReason -> a sheet's own eliminations[]), not the raw legacy
    // #916 field alone. Reading only ride.termReason (the old code here)
    // misses a ride eliminated purely via the SHEET-LEVEL eliminations[]
    // path — no legacy field ever gets stamped for that path, so the
    // announcer saw a bare row with no elimination indicator at all. The
    // sheets passed in cover tier 4 (a not-yet-backfilled legacy row, #2056)
    // for rides where score-api's own write-back hasn't denormalized the
    // elimination onto ride.terminationReason yet.
    termReason: deriveRideTermReason(ride, sheetsByRideId?.get(ride.id) ?? []) || "",
    // #643 ride-detail accordion fields — see comment above.
    horseDetail,
    pedigree,
    ownerLine,
    address,
    // The live poll wins over the (possibly stale) prefetched row.
    status: normalizeStatus(statuses?.[ride.id] ?? ride.status),
    role: null,
    ride,
  };
}

function buildBreakRow(brk) {
  return {
    kind: ROW_KIND.BREAK,
    id: brk.id,
    scheduledTime: brk.scheduledTime,
    sortKey: timeKey(brk.scheduledTime),
    // #892 — see the matching comment on buildRideRow above.
    dayKey: venueDayKey(brk.scheduledTime),
    label: brk.label || "",
  };
}

// #2131 (docs/score/ring-truth.md) — CURRENT/ON_DECK now come from the ring's
// OWN stored ring-state row via resolveRingTruth/resolveNextRide (score-common
// #2108), not from hunting this page's whole ride list for an IN_PROGRESS
// status the way #858's assignRideRoles did. That hunt is exactly the bug
// (#2083/#2131): a ride's open scoresheets alone make it read IN_PROGRESS
// (rule 3 explicitly rejects that as "in progress"), and nothing revoked the
// status when the scribe moved on to a later class, so a 9am ride could still
// win CURRENT at 2pm. The ring-state row is revoked positively instead — a
// scribe starting a new class overwrites it (rule 4) — so there is no ghost
// to filter out here.
//
// Rule 8 — with no valid ring state (`ringTruth.inProgress` false), no role
// is assigned at all. No class is invented, nothing here guesses.
//
// classOrderInRing is the order class headers were pushed while building
// `rows` above — i.e. this ring's own running order, the same data this
// function already has, not a second class fetch. Rule 7's "next class in
// that ring" reads directly off it.
function assignRoles(rows, ringTruth, classOrderInRing) {
  const rideRows = rows.filter((r) => r.kind === ROW_KIND.RIDE);
  for (const row of rideRows) row.role = null;
  if (!ringTruth?.inProgress) return rows;

  const liveClassRides = rideRows.filter((r) => r.classId === ringTruth.classId);
  const liveIdx = classOrderInRing.indexOf(ringTruth.classId);
  const nextClassId = liveIdx >= 0 ? classOrderInRing[liveIdx + 1] : undefined;
  const nextClassRides = nextClassId
    ? rideRows.filter((r) => r.classId === nextClassId)
    : [];

  const currentRow = liveClassRides.find((r) => r.id === ringTruth.rideId);
  if (currentRow) currentRow.role = RIDE_ROLE.CURRENT;

  const nextRide = resolveNextRide({ liveClassRides, nextClassRides });
  if (nextRide) {
    const onDeckRow = rideRows.find((r) => r.id === nextRide.id);
    // Guard rather than assume: resolveNextRide echoes back whichever ride
    // object the caller passed it, so this is always found when liveClassRides/
    // nextClassRides were built from rideRows in the first place — but never
    // silently overwrite CURRENT if IDs somehow collide.
    if (onDeckRow && onDeckRow.role !== RIDE_ROLE.CURRENT) onDeckRow.role = RIDE_ROLE.ON_DECK;
  }

  return rows;
}

/**
 * Merge rides + breaks into one time-ordered list and insert class headers.
 *
 * Class headers are *dividers*, not timed rows: a class's position comes from
 * its FIRST ride's time, so a header is emitted the first time a ride of that
 * class is encountered. A break falling mid-class does NOT re-emit the header.
 *
 * #2073 — root cause of "the same class twice, one copy stuck in skeleton
 * loaders": this used to compare only against the IMMEDIATELY PRECEDING ride's
 * classId (`row.classId !== lastClassId`), on the unstated assumption that a
 * class's rides always sort contiguously. A live daysheet doesn't guarantee
 * that — a single rescheduled/late-added ride from another class landing
 * between two of THIS class's rides (real case: Bello Acres Ring 1, class
 * 634) makes the class's id "leave and come back", so the old check saw a
 * second "first ride of a new class" and pushed a SECOND header for the SAME
 * classId. Both headers then shared the same `id` (`class-header-${classId}`,
 * unchanged below) as their React key in OrderOfGo.jsx. A duplicate React key
 * is the LIKELY mechanism for a ClassResults instance getting orphaned
 * mid-fetch — the "stuck skeleton forever" half of this bug — but that
 * specific 200-response path was not reproduced directly (the Cypress
 * skeleton case forces a 500, a different failure useClassResults.js's
 * #2073 fix closes on its own; see that fix for the other, confirmed half: a
 * class-results fetch that fails with no cache must still SETTLE, never
 * hang). Tracking every
 * classId a header has EVER been emitted for (not just the last one) fixes
 * the grouping at its source — each class gets exactly one header, wherever
 * its first ride sorts, and the key collision this caused is gone because
 * there is only ever one header per classId to key. This is NOT a
 * render-time dedupe: `rows` is the one array role assignment, ClassResults
 * keying, and rendering all read, so this is the single source of truth
 * fixed once, not a cosmetic filter layered on top of it.
 *
 * #2131 (docs/score/ring-truth.md) — `ringState` is this ring's own stored
 * ring-state row (or null/undefined for none), the SAME shape scribe-fe's
 * useRingState hook reads via GET /ring-state/:ring. CURRENT/ON_DECK role
 * assignment below is resolved from it via resolveRingTruth/resolveNextRide
 * (score-common #2108), not derived from ride statuses.
 *
 * @returns {Array} rows, each { kind, id, ..., role } — see ROW_KIND. `role`
 *   is null on every row when the ring has no valid in-progress state (rule
 *   8) — no class is ever invented to fill CURRENT/ON_DECK.
 */
export function buildOrderOfGoRows({
  rides = [],
  breaks = [],
  classes = [],
  people = [],
  horses = [],
  tests = [],
  statuses = {},
  ringState = null,
  // #2070 — Map<rideId, ScoreSheet[]>, threaded to deriveRideTermReason so
  // tier 4 (a sheet's own eliminations[]) resolves. Defaults to empty so
  // every existing caller/test that doesn't pass it keeps working exactly
  // as before (tiers 1-3 still resolve off the ride fields alone).
  sheetsByRideId = new Map(),
} = {}) {
  const ctx = {
    classesById: byId(classes),
    peopleById: byId(people),
    horsesById: byId(horses),
    testsById: byId(tests),
    statuses,
    sheetsByRideId,
  };

  const timed = [
    ...(rides || []).filter(Boolean).map((r) => buildRideRow(r, ctx)),
    ...(breaks || []).filter(Boolean).map(buildBreakRow),
  ].sort((a, b) => {
    if (a.sortKey !== b.sortKey) return a.sortKey < b.sortKey ? -1 : 1;
    // Same minute: keep the running order stable and deterministic.
    if (a.kind !== b.kind) return a.kind === ROW_KIND.RIDE ? -1 : 1;
    return String(a.id).localeCompare(String(b.id));
  });

  const rows = [];
  // #2073 — every classId a header has EVER been pushed for, not merely the
  // previous ride's classId. See the function-level comment above.
  const headeredClassIds = new Set();
  for (const row of timed) {
    if (row.kind === ROW_KIND.RIDE && row.classId && !headeredClassIds.has(row.classId)) {
      headeredClassIds.add(row.classId);
      rows.push({
        kind: ROW_KIND.CLASS_HEADER,
        id: `class-header-${row.classId}`,
        classId: row.classId,
        className: row.className,
        classNumber: row.classNumber,
        // #892 — a class header always sits directly in front of the ride
        // that triggered it (pushed in the very same loop iteration, right
        // below), so it belongs to that ride's day.
        dayKey: row.dayKey,
      });
    }
    rows.push(row);
  }

  // Insertion order == this ring's own class running order (each classId was
  // added the first time its first ride was encountered in `timed`, which is
  // already time-sorted) — rule 7's "next class in that ring", read straight
  // off data this function already built, no second class fetch.
  // #2131 — `rides`/`breaks` arrive here ALREADY day-filtered when the
  // announcer's default today-only view is on (OrderOfGo.jsx filters the raw
  // entities before calling this function), so this ordering — and
  // therefore rule 7's roll-to-next-class — is scoped to the visible day on
  // purpose: a class with no ride today never becomes "next" here even if it
  // is next in the full multi-day daysheet. Believed correct (an announcer
  // watching today's view shouldn't be handed tomorrow's class as "next"),
  // but unverified by a test either way.
  const classOrderInRing = [...headeredClassIds];
  const ringTruth = resolveRingTruth(ringState);
  return assignRoles(rows, ringTruth, classOrderInRing);
}

// #2131 (docs/score/ring-truth.md, judge finding (d)) — the ring's "live"
// class for results-polling purposes (ClassResults' `isLive` prop) is the
// ring-state row's OWN classId whenever it's in progress, never derived by
// finding a row with role CURRENT. `ringTruth.rideId` is nullable (rule 5 /
// AC17: Start class names a class before any of its rides has been opened,
// apps/score-api/src/lib/ring-state.js buildExplicitRingState), so a
// RUNNING row can be genuinely in progress with NO ride carrying
// RIDE_ROLE.CURRENT yet — `assignRoles` above never assigns CURRENT when
// `ringTruth.rideId` doesn't match any row's id. Reading `rows` for a
// CURRENT-tagged row in that shape silently loses the live class (and with
// it, the fast results-poll cadence) even though the class genuinely IS in
// progress. Reading `ringTruth.classId` directly has no such gap: it's the
// same field `assignRoles` already trusts for "which class is live".
export function resolveLiveClassId(ringTruth) {
  return ringTruth?.inProgress ? ringTruth.classId ?? null : null;
}

export function findRoleRideId(rows, role) {
  return rows.find((r) => r.kind === ROW_KIND.RIDE && r.role === role)?.id || null;
}

// #892 — the all-rides/today-only toggle's grouping pass. Walks the
// already-built, already-time-ordered `rows` and inserts one DAY_HEADER row
// wherever the venue-local day changes, using each row's own `dayKey` (set
// alongside `sortKey` in buildRideRow/buildBreakRow/the class-header push
// above — the SAME #688/#805 venue-zone conversion every displayed time
// already goes through, so a ride whose UTC calendar day differs from its
// venue-local day still groups under the venue day the printed day sheet
// shows it on).
//
// Deliberately NOT part of buildOrderOfGoRows itself: CURRENT/ON_DECK/NEXT
// role assignment must run over whichever rides/breaks were fetched
// (today-only callers filter the raw entities BEFORE calling
// buildOrderOfGoRows, so the "current ride" is scoped correctly); day-header
// insertion is a pure display concern layered on top afterwards and never
// changes which row holds which role.
//
// A ride/break with no derivable day (dayKey null — no scheduledTime) falls
// back to `unknownDayKey` (the caller's current venue day) rather than
// getting a "no day" bucket of its own: an unscheduled ride is still today's
// business, not a heading nobody asked for.
//
// Calling this even when only one day is present (the today-only view) is
// deliberate, not a special case: it naturally emits exactly one DAY_HEADER,
// at the front, with nothing to repeat — which is exactly "show the single
// day prominently at top, no repeated headings" (#892 AC).
export function groupRowsByDay(rows, unknownDayKey = null) {
  const out = [];
  let lastDayKey;
  for (const row of rows) {
    if (
      row.kind !== ROW_KIND.RIDE &&
      row.kind !== ROW_KIND.BREAK &&
      row.kind !== ROW_KIND.CLASS_HEADER
    ) {
      out.push(row);
      continue;
    }
    const dayKey = row.dayKey || unknownDayKey;
    if (dayKey !== lastDayKey) {
      out.push({ kind: ROW_KIND.DAY_HEADER, id: `day-header-${dayKey}`, dayKey });
      lastDayKey = dayKey;
    }
    out.push(row);
  }
  return out;
}

// #691 — the sticky header's countdown: the next scheduled ride OR break,
// whichever comes first. `rows` is already time-sorted by
// buildOrderOfGoRows, so this is just the first RIDE/BREAK row whose
// scheduledTime is still ahead of `nowMs` — a real instant comparison
// (Date math is timezone-agnostic; only DISPLAY needs the venue-zone
// formatter). Class headers are dividers, never a candidate event.
export function findNextEvent(rows, nowMs = Date.now()) {
  for (const row of rows) {
    if (row.kind !== ROW_KIND.RIDE && row.kind !== ROW_KIND.BREAK) continue;
    if (!row.scheduledTime) continue;
    const t = new Date(row.scheduledTime).getTime();
    if (Number.isNaN(t) || t <= nowMs) continue;
    return row;
  }
  return null;
}

// #644 — results/placings. GET /class/:classId/results (score-api) returns one
// entry per Ride: { rideId, classId, placing, composite, status, ... }, already
// sorted (placing asc, unplaced last, then composite desc) — NEVER re-sorted
// here. But it carries no rider/horse/CNO — reconcile stores ids only, same as
// every other entity in this app — so join those back from the SAME ride rows
// already built by buildOrderOfGoRows, exactly like #643's ride-detail fields.
export function buildClassResults(results = [], rideRows = []) {
  const rideById = byId(rideRows);
  return (results || []).map((r) => {
    const row = rideById.get(r.rideId);
    return {
      rideId: r.rideId,
      classId: r.classId,
      placing: r.placing ?? null,
      composite: r.composite,
      status: r.status,
      // #699 — carry the per-judge confirmation detail (score-api #697) through
      // so RideRow/ClassResults can render committed indicators + the CONFIRMED
      // reveal. perJudge under PARTIAL exposes NO percentage (anti-leak), by design.
      perJudge: r.perJudge ?? [],
      confirmedCount: r.confirmedCount ?? 0,
      completedCount: r.completedCount ?? 0,
      expectedJudgeCount: r.expectedJudgeCount ?? 0,
      competitorNumber: row?.competitorNumber || "",
      riderName: row?.riderName || "",
      horseName: row?.horseName || "",
    };
  });
}

// score-api's per-ride results status (#697): scores are WITHHELD while PARTIAL
// (composite null, no per-judge percentage) so one judge's mark never renders as
// the final score; CONFIRMED = every expected judge confirmed their marks (score
// revealed, "ready to announce"); FINAL = signed/admin-confirmed (placing shown).
// SCRATCHED (#920) = the Ride is scratched and was never judged at all — it
// carries no score, ever, so it's neither "revealed" nor "withheld"; it just
// doesn't participate in either gate below.
export const RESULT_STATUS = {
  PARTIAL: "PARTIAL",
  CONFIRMED: "CONFIRMED",
  FINAL: "FINAL",
  SCRATCHED: "SCRATCHED",
};

// A ride's numeric score is exposed once CONFIRMED or FINAL — never PARTIAL.
// A SCRATCHED ride has no score to reveal either way.
export function isResultRevealed(status) {
  return status === RESULT_STATUS.CONFIRMED || status === RESULT_STATUS.FINAL;
}

// #920 — SCRATCHED rides (scratch/no-show/elim, never judged) are excluded
// from both "every ride" reveal gates below: a scratched ride can never reach
// CONFIRMED/FINAL (it's never scored), so counting it against the class held
// the WHOLE placed class's results hostage on a single non-scored ride — the
// per-ride badge already showed the placing (driven by result.status on the
// SCORED rides, unaffected by this), while the class summary disagreed and
// said "not available yet". Filtering scratched rides out of the `every()`
// check restores agreement without weakening the #699 anti-leak intent: a
// scratch was never going to leak a score (it has none), only a genuinely
// still-scoring PARTIAL ride keeps blocking the gate now.
function scoreableResults(results) {
  return (results || []).filter((r) => r.status !== RESULT_STATUS.SCRATCHED);
}

// #699 — a class's scores become readable once EVERY SCORED ride is
// CONFIRMED-or-FINAL (all judges confirmed), not only when every ride is
// signed FINAL. An empty/unfetched list is never revealed: that would flash a
// misleadingly empty table before data even loads (docs/score/announcer.md:
// "never a half-computed or misleading placing"). A class that is nothing BUT
// scratches has no placings to reveal either, so that degenerate case stays
// unrevealed too.
export function isClassResultsRevealed(results) {
  const scoreable = scoreableResults(results);
  return scoreable.length > 0 && scoreable.every((r) => isResultRevealed(r.status));
}

// A class is FINAL once every SCORED ride has a signed/admin-confirmed FINAL
// result (score-api's per-ride FINAL, #506 admin force-final included) — the
// SAME "allFinal" convention scoreadmin-fe's ClassPlacings.jsx uses, with
// SCRATCHED rides excluded for the same reason as isClassResultsRevealed above.
export function isClassResultsFinal(results) {
  const scoreable = scoreableResults(results);
  return scoreable.length > 0 && scoreable.every((r) => r.status === RESULT_STATUS.FINAL);
}

// #1647: a later poll must never blank an already-revealed class's placings
// (reported live: finalizing a ride flipped a shown box back to "not final",
// discarding results the announcer was mid-read on). isClassResultsRevealed
// above is deliberately unlatched, recomputed from whichever snapshot is
// newest, because the FORWARD direction still needs that: a still-scoring
// class must never leak an early score. So the guard belongs at the single
// choke point new snapshots enter state (useClassResults.js's refresh), not
// inside that predicate.
//
// This is a MONOTONIC reveal, not a freeze: once `next` is itself revealed,
// or `prev` was never revealed to begin with, adopt `next` as usual (a
// genuinely newer, still-revealed snapshot, e.g. a ride moving CONFIRMED to
// FINAL, always applies). Only refuse to fall backward from a revealed
// `prev` to an unrevealed `next` (a ride's status regressing to PARTIAL, an
// empty/null poll response, a not-active reset). A legitimate re-score that
// truly un-reveals a class (a reopened, un-confirmed sheet) shows stale
// placings for one extra tick instead of blanking, which is the safer
// failure for an announcer reading off the screen.
export function preserveRevealedResults(prev, next) {
  if (isClassResultsRevealed(prev) && !isClassResultsRevealed(next)) return prev;
  return next;
}
